import { runInAction } from "mobx";
import type { FetchByQueryResult, Segment } from "mobx-repository";
import { prop } from "ramda";
import type { PageInfo, PublicSetting, PublicSettingsApi, PublicSettingUpdate } from "../../api";
import { FileType } from "../../api";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";
import type { ServiceFile } from "../services/service-file";

export interface PublicSettingsApiResource extends ApiResource {
    entity: PublicSetting;
    query: void;
    update: PublicSettingUpdate;
    create: void;
    sortKey: string;
}

export class RepositoryPublicSettings extends ElofleetRepository<PublicSettingsApiResource> {
    constructor(
        private readonly publicSettingsApi: PublicSettingsApi,
        private readonly serviceFile: ServiceFile,
    ) {
        super();
    }

    public validation = {
        update: new Validation((publicSettingUpdate: PublicSettingUpdate, id: UUID) =>
            this.publicSettingsApi.publicSettingsValidateUpdatePublicSetting({
                publicSettingUpdate,
                id,
            }),
        ),
    };

    protected async fetchById(publicSettingId: UUID): Promise<PublicSetting | undefined> {
        return this.publicSettingsApi.publicSettingsReadPublicSettingById({ id: publicSettingId });
    }

    protected extractId = prop("id");

    protected async fetchByQuery(
        _query: void,
        _pagination: Segment,
    ): Promise<FetchByQueryResult<PublicSetting>> {
        return { entities: [await this.publicSettingsApi.publicSettingsReadPublicSetting()] };
    }

    public fetchMetadata(_: unknown): Promise<PageInfo> {
        throw new Error("Cannot list setting metadata: Method not implemented.");
    }

    public async update(
        id: UUID,
        publicSettingUpdate: PublicSettingUpdate,
        file?: Uint8Array,
    ): Promise<PublicSetting> {
        const publicSetting = await this.publicSettingsApi.publicSettingsUpdatePublicSetting({
            publicSettingUpdate,
            id,
        });

        if (file !== undefined) {
            const logoId = await this.serviceFile.createImageFile(
                id,
                FileType.PUBLIC_SETTING_LOGO,
                file,
            );
            if (logoId) {
                publicSetting.logoId = logoId;
            }
        }

        await this.waitForIdle();
        runInAction(() => {
            // This reload is necessary to reload all queries that want to display "all" entities.
            this.reloadQuery();
            this.add(publicSetting);
        });

        return publicSetting;
    }

    public create(_settingCreate: never): Promise<PublicSetting> {
        throw new Error("Cannot create new public setting: Method not implemented.");
    }

    public delete(..._ids: string[]): Promise<void> {
        throw new Error("Cannot delete public setting: Method not implemented.");
    }
}
